import { useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { QrCode01 } from "@untitledui/icons"
import { toast } from "sonner"
import { useTeam } from "@/lib/api/use-teams"
import { useEstimates } from "@/lib/api/use-estimates"
import { teamsApi } from "@/lib/api/teams"
import { queryKeys } from "@/lib/api/query-keys"
import type { TeamSettingsMetadata } from "@/lib/api/types"
import { readIntakeMeta } from "@/lib/api/public-consumer"
import {
  resolveIntakeConfig,
  type IntakeConfig,
  type ShopBranding,
} from "@/lib/intake/intake-config"
import { sourceOf } from "@/components/dashboard/room/room-model"
import { Roles } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useAuth } from "@/lib/auth/auth-context"
import { cx } from "@/lib/utils/cx"
import { SetupPanel } from "./setup-panel"
import { DistributePanel } from "./distribute-panel"
import { IntakePreview } from "./intake-preview"

// Settings → Consumer intake — the shop's public photo-first front door
// (/go, /embed, /kiosk) configured and distributed from one place.
//
// Storage: team.metadata.intake + metadata.branding, saved through the REAL
// PUT /v1/teams/{id}/settings as a read-modify-write (never clobber the
// other metadata blocks — same pattern as booking-calendar-settings). The
// public flow reads it back through the mock GET /public/shops/:id.

type Tab = "setup" | "distribute"

// Surface → the grouping a shop owner actually thinks in. embed + widget are
// both "their website"; link + sms are the same URL travelling by hand.
function surfaceGroup(surface: string): "website" | "QR" | "kiosk" | "link" {
  if (surface === "embed" || surface === "widget") return "website"
  if (surface === "qr") return "QR"
  if (surface === "kiosk") return "kiosk"
  return "link"
}

function resolveBranding(raw: unknown, teamName: string): ShopBranding {
  const r =
    typeof raw === "object" && raw !== null && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const initialsFromName = teamName
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase()
  return {
    primaryColor: typeof r.primaryColor === "string" ? r.primaryColor : "#059669",
    logoUrl: typeof r.logoUrl === "string" && r.logoUrl.length > 0 ? r.logoUrl : null,
    logoInitials:
      typeof r.logoInitials === "string" && r.logoInitials.length > 0
        ? r.logoInitials
        : initialsFromName || null,
    hours: typeof r.hours === "string" ? r.hours : "",
  }
}

interface FormState {
  intake: IntakeConfig
  branding: ShopBranding
}

export function ConsumerIntakeSettings({ teamId }: { teamId: string }) {
  const { data: team, isLoading } = useTeam(teamId)
  const { data: estimates = [] } = useEstimates(teamId)
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<Tab>("setup")

  // Same manager gate as Booking calendar / Marketing Supercharge — the
  // public form's copy, consent line, and on/off switch are a manager's call.
  const role = useCurrentRole()
  const { user } = useAuth()
  const canManage =
    !!user?.admin ||
    role === Roles.Director ||
    role === Roles.GeneralManager ||
    role === Roles.ServiceManager

  const [form, setForm] = useState<FormState | null>(null)
  const [initial, setInitial] = useState<FormState | null>(null)

  const dirty = useMemo(
    () => !!form && !!initial && JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  )
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty

  // Hydrate from the team, but never clobber in-progress edits on a refetch
  // of the SAME team (markShared invalidates the team query mid-edit).
  const hydratedForRef = useRef<string | null>(null)
  useEffect(() => {
    if (!team) return
    if (hydratedForRef.current === teamId && dirtyRef.current) return
    const meta = (team.metadata ?? {}) as TeamSettingsMetadata
    const next: FormState = {
      intake: resolveIntakeConfig(meta.intake),
      branding: resolveBranding(meta.branding, team.name),
    }
    setForm(next)
    setInitial(next)
    hydratedForRef.current = teamId
  }, [team, teamId])

  // Header stats — leads this week by surface, from the team's estimates.
  const stats = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 3600 * 1000
    const week = estimates.filter(
      (e) =>
        sourceOf(e) === "consumer_intake" && new Date(e.createdAt).getTime() >= weekAgo,
    )
    const groups = new Map<string, number>()
    for (const e of week) {
      const g = surfaceGroup(readIntakeMeta(e)?.surface ?? "link")
      groups.set(g, (groups.get(g) ?? 0) + 1)
    }
    const breakdown = (["website", "QR", "kiosk", "link"] as const)
      .filter((g) => (groups.get(g) ?? 0) > 0)
      .map((g) => `${g} ${groups.get(g)}`)
      .join(" · ")
    return { count: week.length, breakdown }
  }, [estimates])

  const save = async () => {
    if (!team || !form || saving) return
    setSaving(true)
    try {
      // Read-modify-write — never clobber the other metadata blocks.
      await teamsApi.updateSettings(Number(teamId), {
        metadata: {
          ...(team.metadata as TeamSettingsMetadata),
          branding: form.branding,
          intake: { ...form.intake, updatedAt: new Date().toISOString() },
        },
      })
      setInitial(form)
      qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      toast.success("Consumer intake updated")
    } catch {
      toast.error("Couldn't save. Try again.")
    } finally {
      setSaving(false)
    }
  }

  // First distribution action stamps sharedAt ONCE (clears the sidebar's
  // amber glyph). Writes the SAVED config + the stamp — never the unsaved
  // form, so copying a link can't silently commit half-edited settings.
  const sharingRef = useRef(false)
  const markShared = () => {
    if (!team || !canManage || sharingRef.current) return
    const meta = (team.metadata ?? {}) as TeamSettingsMetadata
    const saved = resolveIntakeConfig(meta.intake)
    if (saved.sharedAt) return
    sharingRef.current = true
    const sharedAt = new Date().toISOString()
    teamsApi
      .updateSettings(Number(teamId), {
        metadata: {
          ...(team.metadata as TeamSettingsMetadata),
          intake: { ...saved, sharedAt },
        },
      })
      .then(() => {
        // Keep the local form/initial in step so the refetch doesn't read
        // as an unsaved change.
        setForm((f) => (f ? { ...f, intake: { ...f.intake, sharedAt } } : f))
        setInitial((f) => (f ? { ...f, intake: { ...f.intake, sharedAt } } : f))
        qc.invalidateQueries({ queryKey: queryKeys.teams.detail(teamId) })
      })
      .catch(() => {
        sharingRef.current = false
      })
  }

  if (isLoading || !team || !form || !initial) {
    return <div className="h-64 animate-pulse rounded-2xl border border-zinc-200/70 bg-white" />
  }

  const enabled = form.intake.enabled

  return (
    <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_auto] xl:items-start xl:gap-8">
      <div className="min-w-0 max-w-3xl space-y-5">
        {/* Charcoal header band — identity, live status, this week's receipts. */}
        <div className="overflow-hidden rounded-2xl bg-zinc-900 text-white shadow-xl shadow-zinc-900/10">
          <div className="px-5 py-5 md:px-7 md:py-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/25">
                  <QrCode01 className="size-5 text-white" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                    Consumer intake
                  </p>
                  <h2 className="text-[18px] font-bold leading-tight text-white">
                    Customers start their own estimate
                  </h2>
                </div>
              </div>
              <span
                className={cx(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide",
                  enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300",
                )}
              >
                <span
                  aria-hidden
                  className={cx(
                    "size-1.5 rounded-full",
                    enabled ? "bg-emerald-400" : "bg-amber-400",
                  )}
                />
                {enabled ? "Live" : "Paused"}
              </span>
            </div>
            <p className="mt-3 max-w-lg text-[13px] leading-relaxed text-zinc-400">
              A link, QR code, website form, and lobby kiosk where car owners
              send damage photos — each one lands here as a lead with the
              AI read already running.
            </p>
            {!canManage && (
              <p className="mt-2 text-[11.5px] text-zinc-500">
                Only a manager can change these settings.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-white/10 px-5 py-3.5 md:px-7">
            <span className="text-[22px] font-bold tabular-nums text-emerald-400">
              {stats.count}
            </span>
            <span className="text-[13px] text-zinc-400">
              lead{stats.count === 1 ? "" : "s"} this week
            </span>
            {stats.breakdown && (
              <>
                <span aria-hidden className="text-zinc-600">
                  ·
                </span>
                <span className="text-[13px] text-zinc-400">{stats.breakdown}</span>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-5 border-b border-zinc-200" role="tablist">
          <TabButton
            active={tab === "setup"}
            label="Set up"
            onClick={() => setTab("setup")}
          />
          <TabButton
            active={tab === "distribute"}
            label="Share & embed"
            onClick={() => setTab("distribute")}
          />
        </div>

        {tab === "setup" ? (
          // One native gate for every control inside — a non-manager sees
          // the configuration but can't operate it.
          <fieldset disabled={!canManage} className="min-w-0 disabled:opacity-70">
            <SetupPanel
              intake={form.intake}
              branding={form.branding}
              shopName={team.name}
              onIntake={(patch) =>
                setForm((f) => (f ? { ...f, intake: { ...f.intake, ...patch } } : f))
              }
              onBranding={(patch) =>
                setForm((f) => (f ? { ...f, branding: { ...f.branding, ...patch } } : f))
              }
            />
          </fieldset>
        ) : (
          <DistributePanel
            shopId={teamId}
            shopName={team.name}
            shopPhone={team.phoneNumber}
            branding={form.branding}
            onShared={markShared}
          />
        )}

        {dirty && canManage && (
          <div className="sticky bottom-4 flex items-center justify-between gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-lg">
            <p className="text-[13px] text-zinc-600">Unsaved changes</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setForm(initial)}
                className="h-9 rounded-xl px-3 text-[12.5px] font-semibold text-zinc-600 hover:bg-zinc-50"
              >
                Discard
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={save}
                className="h-9 rounded-xl bg-zinc-900 px-4 text-[12.5px] font-semibold text-white hover:bg-black disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right rail — the phone the flow will actually run on, live with
          unsaved edits. xl+ only; smaller screens rely on "Open" links. */}
      <div className="sticky top-6 hidden xl:block">
        <IntakePreview shopId={teamId} intake={form.intake} branding={form.branding} />
      </div>
    </div>
  )
}

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cx(
        "-mb-px border-b-2 pb-2.5 pt-1 text-[13.5px] font-semibold transition-colors",
        active
          ? "border-emerald-600 text-zinc-900"
          : "border-transparent text-zinc-500 hover:text-zinc-800",
      )}
    >
      {label}
    </button>
  )
}
